import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { FilterInHouseButtonItems } from '@/bundles/GroupFilters/enums/InHouseFilterEnum';
import {
  FilterAllExcludeFilterEnum,
  FilterInvestmentSaleButtonItems,
  FilterSubleaseButtonItems,
  FilterTransactionStatusButtonItems,
  FilterTransactionTypeEnum,
  FilterTransactionTypeLabels
} from '@/bundles/GroupFilters/enums/transactionType';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { AllExcludeOnlyEnum, AllExcludeOnlyLabels } from '@/bundles/GroupFilters/enums/AllExcludeOnlyEnum';
import { Nullable } from '@/utils/types';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { getAvailabilityStatusItems } from '@/bundles/Availabilities/helpers';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import { FilterPocketListingButtonItems } from '@/bundles/GroupFilters/enums/FilterPocketListingEnum';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import {
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getMarketsFilters,
  getPropertyTypesWithMixedUseForFilters,
  getPropertyUsesForFilters,
  PROPERTY_TYPE_KEY
} from '@/bundles/GroupFilters/consts';
import {
  getCraneFiltersConfigByType,
  getLoadingAndDoorsFiltersConfigByType,
  getParkingFiltersConfigByType,
  getPropertyBuildingFiltersConfig,
  getPropertySiteFiltersConfig,
  getRailFiltersConfigByType,
  getRefrigerationFiltersConfigByType,
  getUtilitiesFiltersConfigByType
} from '@/bundles/GroupFilters/helpers/filtersConfig/reusable';
import { isYearRule } from '@/bundles/GroupFilters/helpers/rules';
import { FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';
import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getVacancyStatuses } from '@/bundles/Availabilities/helpers/availabilityConsts';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

export const getAvailabilityFilters = (filters?: Nullable<IGroupFilterItem>): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();
  const selectedPropertyTypes = filters?.[PROPERTY_TYPE_KEY]?.value || [];

  return [
    // FilterCategoryEnum.availability_info start
    {
      title: 'In House',
      type: FilterTypeEnum.in_house_group,
      category: FilterCategoryEnum.availability_info,
      children: [
        {
          title: 'In House',
          name: 'availability_in_house',
          items: <IFilterButtonGroupItems>FilterInHouseButtonItems,
          defaultValue: AllExcludeOnlyEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Agency Type',
          name: 'availability_agency_type',
          items: ['Exclusive', 'Non-Exclusive', 'Owner Listed', 'Pocket'],
          type: FilterTypeEnum.autocomplete_multiple_select,
        },
      ],
    },
    {
      title: 'Transaction Type',
      type: FilterTypeEnum.transaction_type_group,
      category: FilterCategoryEnum.availability_info,
      children: [
        {
          title: 'Transaction Type',
          name: 'availability_transaction_type',
          items: <IFilterButtonGroupItems>Object.keys(FilterTransactionTypeLabels).map((key) => ({
            text: FilterTransactionTypeLabels[key],
            value: key,
          })),
          defaultValue: FilterTransactionTypeEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Sublease',
          name: 'availability_sublease',
          items: <IFilterButtonGroupItems>FilterSubleaseButtonItems,
          defaultValue: FilterAllExcludeFilterEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Investment Sale',
          name: 'availability_investment',
          items: <IFilterButtonGroupItems>FilterInvestmentSaleButtonItems,
          defaultValue: FilterAllExcludeFilterEnum.all,
          type: FilterTypeEnum.button_group,
        },
        {
          title: 'Transaction Status',
          name: 'availability_transaction_status',
          items: <IFilterButtonGroupItems>FilterTransactionStatusButtonItems,
          type: FilterTypeEnum.button_group,
          additional: {
            mandatory: false,
          }
        },
      ],
    },
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.availability_info,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Availability Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.availability_info,
      children: [
        {
          title: 'Availability Use Class',
          name: 'availability_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Availability Use',
          name: 'availability_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Availability Use Type(s)',
      name: 'availability_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Lease Rate Starting',
      name: 'availability_lease_rate',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.availability_info,
      additional: {
        suffix: '/ SF',
      }
    },
    {
      title: 'Available Space',
      name: 'availability_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
      formatter: (v: Nullable<number>) => formatToSF(v),
      searchKeywords: ['size']
    },
    {
      title: 'Land Size',
      type: FilterTypeEnum.land_size_with_pol_group,
      category: FilterCategoryEnum.availability_info,
      children: [
        {
          title: 'Land Size',
          name: 'availability_land_size',
          type: FilterTypeEnum.land_size_range, // sending with availability_land_size_unit
        },
        {
          title: 'Land Size POL',
          name: 'availability_land_pol',
          type: FilterTypeEnum.switch,
        },
      ],
    },
    {
      title: 'Sale Price',
      name: 'availability_sales_price',
      type: FilterTypeEnum.sale_price_range, // sending with availability_sales_price_unit
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: '# of Units',
      name: 'availability_building_units',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Date On Market',
      name: 'availability_date_listed',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Months On Market',
      name: 'availability_months_on_market',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Office Size',
      name: 'availability_office_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Min Clear Height',
      name: 'availability_min_clear_height',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Max Clear Height',
      name: 'availability_max_clear_height',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: '# Dock High Doors',
      name: 'availability_dock_door_count',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: '# Drive-In Doors',
      name: 'availability_ground_door_count',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Year Built',
      name: 'availability_year_built',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: i18n.t(LocalizationEnum.target_completion_year) as string,
      name: 'property_target_completion_year',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.availability_info,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: 'Sublease Expiration Date',
      name: 'sublease_expiration_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Availability Status',
      name: 'availability_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getAvailabilityStatusItems().map(item => ({
        text: item.text,
        value: item.value.toLowerCase()
      })),
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Vacancy Status',
      name: 'availability_vacancy_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getVacancyStatuses(),
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Verification Status',
      name: 'verification_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: [
        { text: 'Verified', value: 'verified' },
        { text: 'Pending', value: 'pending' },
        { text: 'Expired', value: 'expired' },
      ],
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: 'Pocket Listing',
      name: 'availability_pocket',
      items: <IFilterButtonGroupItems>FilterPocketListingButtonItems,
      type: FilterTypeEnum.button_group,
      category: FilterCategoryEnum.availability_info,
      additional: {
        mandatory: false,
      }
    },
    {
      title: 'Published To Web',
      name: 'availability_published_to_web', // should be handled in mappers to old filters
      items: <IFilterButtonGroupItems>Object.keys(AllExcludeOnlyLabels).map((key) => ({
        text: AllExcludeOnlyLabels[key],
        value: key,
      })),
      type: FilterTypeEnum.button_group,
      category: FilterCategoryEnum.availability_info,
      additional: {
        mandatory: false,
      }
    },
    {
      title: 'Availability Comments',
      name: 'availability_comments',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.availability_info,
    },
    {
      title: i18n.t(LocalizationEnum.legacy_id) as string,
      name: 'availability_legacy_id',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.availability_info,
    },
    // FilterCategoryEnum.availability_info end
    // FilterCategoryEnum.site start
    ...getPropertySiteFiltersConfig(),
    // FilterCategoryEnum.site end
    // FilterCategoryEnum.building start
    ...getPropertyBuildingFiltersConfig({
      propertyTypesWithMixedUse, type: 'availability'
    }),
    // FilterCategoryEnum.building end
    // FilterCategoryEnum.crane start
    ...getCraneFiltersConfigByType(),
    // FilterCategoryEnum.crane end
    // FilterCategoryEnum.parking start
    ...getParkingFiltersConfigByType('availability'),
    // FilterCategoryEnum.parking end
    // FilterCategoryEnum.rail start
    ...getRailFiltersConfigByType('availability'),
    // FilterCategoryEnum.rail end
    // FilterCategoryEnum.refrigeration start
    ...getRefrigerationFiltersConfigByType('availability'),
    // FilterCategoryEnum.refrigeration end
    // FilterCategoryEnum.doors start
    ...getLoadingAndDoorsFiltersConfigByType('availability'),
    // FilterCategoryEnum.doors end
    // FilterCategoryEnum.utilities start
    ...getUtilitiesFiltersConfigByType('availability'),
    // FilterCategoryEnum.utilities end
    // FilterCategoryEnum.market_location start
    {
      title: 'Address',
      name: 'property_address',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'City',
      name: 'property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.market_location,
      additional: {
        citiesSource: 'property',
      }
    },
    {
      title: 'State',
      name: 'property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Country',
      name: 'property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Zip / Postal Code',
      name: 'availability_zip_code',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.market_location,
    },
    ...getMarketsFilters({ propertyTypes: selectedPropertyTypes }),
    {
      title: `Property ${i18n.t(LocalizationEnum.apn_number)}`,
      name: 'property_apn_number',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    // FilterCategoryEnum.market_location end
    // FilterCategoryEnum.company_contact start
    {
      title: 'Company Name',
      name: 'availability_company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Name',
      name: 'contact_names',
      type: FilterTypeEnum.contact_names_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Company Role',
      name: 'contact_company_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('company'),
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Availability Role',
      name: 'availability_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('availability'),
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Availability Brokers',
      name: 'availability_broker',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.company_contact,
    },
    // FilterCategoryEnum.company_contact end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'availability_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'availability_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'availability_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'availability_view',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'availability_created_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'availability_edited_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('availability'),
  ].filter(Boolean) as IFilterConfigItem[]; // filter out null values
};
